import React, { useState, useEffect } from 'react';
import { X, User, Shield, Calendar, Save, Settings, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../UI/LoadingSpinner';

interface Contact {
  pk: number;
  firstname: string;
  lastname: string;
  email: string;
  time_zone: string;
  mobile?: string;
  admin_group: boolean;
  billing_group: boolean;
  technical_group: boolean;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
  user: Contact | null;
}

interface ServiceLicense {
  pk: number;
  name: string;
  description: string;
  category: string;
  checked: boolean;
  available: boolean;
  licenseId?: number;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, onUserUpdated, user }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'licenses'>('profile');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<ServiceLicense[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Removing a Voice360 license deletes the user's extension, so it takes a second click
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    admin_group: false,
    billing_group: false,
    technical_group: false,
    time_zone: 'US/Eastern',
  });

  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const timezones = [
    'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'
  ];

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        mobile: user.mobile || '',
        admin_group: user.admin_group || false,
        billing_group: user.billing_group || false,
        technical_group: user.technical_group || false,
        time_zone: user.time_zone || 'US/Eastern',
      });
      loadUserServices();
    }
  }, [isOpen, user]);

  const loadUserServices = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Get all available services
      const servicesResponse = await apiService.getServices();
      // Get user's current licenses  
      const licensesResponse = await apiService.getUserLicenses(user.pk);

      if (servicesResponse.result) {
        const userLicenses = licensesResponse.result || [];
        
        // Filter only Voice360 services (itp_voice)
        const voice360Services = servicesResponse.result.filter((service: any) => 
          service.item_type === 'itp_voice'
        );
        
        const servicesWithStatus = voice360Services.map((service: any) => {
          const userLicense = userLicenses.find((license: any) => license.service_id === service.pk);
          
          return {
            pk: service.pk,
            name: service.product.name,
            description: service.product.description,
            category: service.product.category,
            checked: !!userLicense,
            available: true,
            licenseId: userLicense?.pk,
          };
        });
        
        setServices(servicesWithStatus);
      }
    } catch (err: any) {
      console.error('Error loading user services:', err);
      setError('Failed to load user services');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.firstname.trim()) {
      errors.firstname = 'First name is required';
    }
    
    if (!formData.lastname.trim()) {
      errors.lastname = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const pendingRemovals = services.filter(service => !service.checked && service.licenseId);

  const handleServiceToggle = (serviceId: number) => {
    setConfirmRemoval(false);
    setServices(prev => prev.map(service => 
      service.pk === serviceId 
        ? { ...service, checked: !service.checked }
        : service
    ));
  };

  const handleUpdateProfile = async () => {
    if (!user || !validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      await apiService.updateUser(user.pk, formData);
      onUserUpdated();
      setError(null);
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLicenses = async () => {
    if (!user) return;
    if (pendingRemovals.length > 0 && !confirmRemoval) {
      setConfirmRemoval(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Process license changes
      for (const service of services) {
        if (service.checked && !service.licenseId) {
          // Add new license
          await apiService.addUserLicense(user.pk, service.pk);
        } else if (!service.checked && service.licenseId) {
          // Remove existing license
          await apiService.removeUserLicense(user.pk, service.licenseId);
        }
      }

      setConfirmRemoval(false);
      onUserUpdated();
      setError(null);
    } catch (err: any) {
      console.error('Error updating licenses:', err);
      setError(err.response?.data?.message || 'Failed to update licenses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      mobile: '',
      admin_group: false,
      billing_group: false,
      technical_group: false,
      time_zone: 'US/Eastern',
    });
    setActiveTab('profile');
    setConfirmRemoval(false);
    setValidationErrors({});
    setError(null);
    setServices([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
              <p className="text-sm text-gray-600">
                {user.firstname} {user.lastname}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span>Profile</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('licenses')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'licenses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Settings className="h-4 w-4" />
                <span>Licenses</span>
              </div>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.firstname ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {validationErrors.firstname && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.firstname}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.lastname ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {validationErrors.lastname && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.lastname}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter mobile number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  name="time_zone"
                  value={formData.time_zone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Contact Authorizations
                </label>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="admin_group"
                      checked={formData.admin_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-purple-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Account Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to manage the account and contact ITP on all matters</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="billing_group"
                      checked={formData.billing_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-blue-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Billing Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to contact ITP regarding billing and invoices</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="technical_group"
                      checked={formData.technical_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-green-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Technical Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to approve technical changes and service modifications</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'licenses' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Voice360 Licenses</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Manage which Voice360 services this user has access to.
                </p>
              </div>

              {services.length === 0 ? (
                <div className="text-center py-8">
                  <LoadingSpinner />
                  <p className="text-gray-500 mt-2">Loading user licenses...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {services.map(service => (
                    <div key={service.pk} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={service.checked}
                        onChange={() => handleServiceToggle(service.pk)}
                        disabled={!service.available}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-xs text-gray-500">{service.description}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {service.category}
                            </span>
                            {service.licenseId && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                        {service.licenseId && !service.checked && (
                          <p className="mt-2 text-xs text-amber-700">
                            Removing this license deletes the user's Voice360 extension.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {confirmRemoval && pendingRemovals.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">
                        Remove {pendingRemovals.map(service => service.name).join(', ')} from {formData.firstname} {formData.lastname}?
                      </p>
                      <p className="mt-1">
                        This deletes their Voice360 extension: phones, voicemail box, call routing,
                        queue memberships and SMS number assignments. It cannot be undone, and adding
                        the license again creates a new extension.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={activeTab === 'profile' ? handleUpdateProfile : handleUpdateLicenses}
            disabled={loading}
            className={`inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
              activeTab === 'licenses' && confirmRemoval ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {activeTab === 'profile' ? 'Update Profile' : confirmRemoval ? 'Remove license' : 'Update Licenses'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
